import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { sdk, makeSdkForIssuer } from '@/lib/tesseraSdk';
import { useAuthStore, type AuthTokens } from '@/stores/authStore';
import { fetchMyRoles } from '@/api/roleSwitch';

/**
 * Handles the OIDC callback from Tessera.
 *
 * 1. sdk.handleCallback() — exchanges the auth code for tokens and
 *    persists them under `kairos.auth` (Zustand's persist key).
 * 2. Push the new tokens into Zustand's *in-memory* state so the
 *    apiClient interceptor can attach the bearer header to the
 *    /whoami call. The SDK already wrote the persisted envelope; this
 *    bridges the runtime view.
 * 3. /api/v1/whoami fetches the kairos-shaped identity (the
 *    TesseraJwtAdapter on the backend translates the Tessera JWT into
 *    kairos.users.id / kairos.tenants.id / kairos role).
 * 4. setSession finalises both user + tokens via Zustand's setter,
 *    which writes the full envelope back to localStorage.
 */
export function TesseraCallbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);
  // Guard against React 18 StrictMode double-firing the effect — the
  // auth code is one-shot, so a duplicate exchange would 4xx the
  // second call.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;

    (async () => {
      // For staff users (@lopettia.ai) in non-prod envs, LoginPage stored the
      // prod Tessera issuer before the OIDC redirect. Use it here so the code
      // exchange goes to the same Tessera that issued the authorization code.
      // The SDK always POSTs to its own configured issuer — using the wrong
      // instance (env Tessera vs prod Tessera) would result in an invalid_grant.
      let pendingIssuer: string | null = null;
      try {
        const raw = localStorage.getItem('kairos.sso.pendingIssuer');
        if (raw) {
          const entry = JSON.parse(raw) as { issuer: string; expiresAt: number };
          if (entry.expiresAt > Date.now()) pendingIssuer = entry.issuer;
        }
      } catch { /* ignore malformed */ }
      localStorage.removeItem('kairos.sso.pendingIssuer');
      const activeSdk = pendingIssuer ? makeSdkForIssuer(pendingIssuer) : sdk;
      const r = await activeSdk.handleCallback();
      if (!r.ok) {
        // recoverable=true: benign session-expiry (PKCE record gone — new tab,
        // storage cleared, abandoned flow). Show the SDK's user-friendly message
        // directly; the "Try again" button restarts the flow cleanly.
        const msg = r.recoverable
          ? (r.errorDescription ?? 'Your sign-in session expired. Please sign in again.')
          : `${r.error}: ${r.errorDescription ?? '(no description)'}`;
        setError(msg);
        return;
      }
      try {
        // Strip to Kairos's AuthTokens shape (no idToken/scope) and
        // push into Zustand's in-memory state so the apiClient
        // interceptor picks it up for the whoami call below.
        const tokens: AuthTokens = {
          accessToken: r.tokens.accessToken,
          accessTokenExpiresAt: r.tokens.accessTokenExpiresAt,
          idToken: r.tokens.idToken,
          refreshToken: r.tokens.refreshToken,
          refreshTokenExpiresAt: r.tokens.refreshTokenExpiresAt,
        };
        useAuthStore.setState({ tokens });

        const { data: whoami } = await apiClient.get<{
          userId: string;
          tenantId: string;
          role: string;
        }>('/whoami');

        setSession(
          {
            userId: whoami.userId,
            tenantId: whoami.tenantId,
            role: whoami.role,
            roles: [whoami.role],
            currentRole: whoami.role,
            passwordResetRequired: false, // Tessera owns password lifecycle
          },
          tokens,
        );

        // Best-effort multi-role hydrate — see LoginPage equivalent.
        void fetchMyRoles()
          .then((res) => useAuthStore.getState().setRoles(res.roles))
          .catch(() => {
            /* non-fatal */
          });

        navigate(r.returnTo, { replace: true });
      } catch (e) {
        setError((e as Error).message ?? 'Login failed.');
      }
    })();
  }, [navigate, setSession]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted p-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 text-center shadow-sm">
        {error ? (
          <>
            <h1 className="mb-3 text-lg font-semibold text-destructive">
              {t('auth.login.error_generic')}
            </h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="mt-4 text-sm text-primary underline"
            >
              {t('auth.login.retry', 'Try again')}
            </button>
          </>
        ) : (
          <>
            <h1 className="mb-3 text-lg font-semibold text-primary">
              {t('auth.login.title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('auth.login.signing_in', 'Signing you in…')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
